import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  User, Link2, Palette, Users, BarChart3, LogOut, Eye,
  CreditCard, Video, Calendar, Shield, Settings, ChevronUp,
  Trash2, Receipt, Crown, X, Menu
} from 'lucide-react';
import { PlexataIcon } from '../components/PlexataLogo';
import ProfileTab from '../components/dashboard/ProfileTab';
import LinksTab from '../components/dashboard/LinksTab';
import ContentTab from '../components/dashboard/ContentTab';
import ThemeTab from '../components/dashboard/ThemeTab';
import ContactsTab from '../components/dashboard/ContactsTab';
import ScheduleTab from '../components/dashboard/ScheduleTab';
import AnalyticsTab from '../components/dashboard/AnalyticsTab';
import PlanTab from '../components/dashboard/PlanTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'links', label: 'Links', icon: Link2 },
  { id: 'content', label: 'Content', icon: Video },
  { id: 'theme', label: 'Theme', icon: Palette },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'plan', label: 'Plan', icon: CreditCard },
];

const Dashboard = () => {
  const { logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [msg, setMsg] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      setProfile(res.data);
    } catch { navigate('/login'); }
  }, [navigate]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setMsg('Payment processing. Check your email for confirmation.'); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setMsg('Payment successful! You are now a Pro member.');
        loadProfile();
        return;
      } else if (res.data.status === 'expired') {
        setMsg('Payment session expired. Please try again.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) { console.error('Payment status check failed:', error); }
  }, [loadProfile]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    if (sessionId && payment === 'success') {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', '/dashboard');
    } else if (payment === 'cancelled') {
      setMsg('Payment cancelled');
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [location.search, pollPaymentStatus]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (!profile) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
    </div>
  );

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(''), 2500); };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/profile/account`);
      logout();
      navigate('/');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <header className="bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 py-3.5">
        <div className="flex items-center gap-2">
          {/* Mobile menu button */}
          <button
            className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-900 transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-logo">
            <PlexataIcon size={24} />
            <span className="font-heading text-lg font-medium tracking-tight text-gray-900">plexata</span>
          </Link>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          {profile.role === 'admin' && (
            <Link to="/admin"
              className="flex items-center gap-1.5 text-xs font-semibold text-[#52ff00] hover:text-[#3dd900] transition-colors bg-[#52ff00]/5 px-3 py-1.5 rounded-full border border-[#52ff00]/20"
              data-testid="admin-panel-link">
              <Shield className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          <a href={`/u/${profile.username}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-indigo-600 transition-colors bg-gray-50 px-3 py-1.5 rounded-full"
            data-testid="preview-card-link">
            <Eye className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Preview Card</span>
          </a>
          <button onClick={() => { logout(); navigate('/'); }}
            className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-red-500 transition-colors"
            data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Mobile Slide-out Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl flex flex-col animate-slide-in-left">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <PlexataIcon size={22} />
                <span className="font-heading text-base font-medium text-gray-900">plexata</span>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-gray-400 hover:text-gray-600" data-testid="close-mobile-menu">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 p-3 space-y-0.5 overflow-y-auto">
              {TABS.map(t => (
                <button key={t.id} onClick={() => { setTab(t.id); setMobileMenuOpen(false); }}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all w-full ${tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                  data-testid={`mobile-tab-${t.id}`}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>

            {/* User Section */}
            <div className="border-t border-gray-100 p-3 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {profile.profile?.image_url ? (
                    <img src={profile.profile.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{profile.profile?.name || 'No name'}</p>
                  <p className="text-xs text-gray-400 truncate">@{profile.username}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${profile.plan === 'pro' ? 'bg-amber-100 text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                  {profile.plan}
                </span>
              </div>
              <div className="space-y-0.5">
                <button onClick={() => { setTab('plan'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  data-testid="mobile-settings-plan">
                  <Crown className="w-4 h-4 text-amber-500" /> Manage Plan
                </button>
                <button onClick={() => { navigate('/billing'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  data-testid="mobile-settings-billing">
                  <Receipt className="w-4 h-4 text-gray-400" /> Billing & Invoices
                </button>
                <button onClick={() => { setMobileMenuOpen(false); setShowDeleteConfirm(true); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                  data-testid="mobile-settings-delete">
                  <Trash2 className="w-4 h-4" /> Delete Account
                </button>
                <button onClick={() => { logout(); navigate('/'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-50 transition-colors"
                  data-testid="mobile-logout-btn">
                  <LogOut className="w-4 h-4" /> Log Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <nav className="hidden md:flex flex-col w-52 bg-white border-r border-gray-200 h-[calc(100vh-53px)] sticky top-[53px]">
          {/* Scrollable tabs area */}
          <div className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all w-full ${tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                data-testid={`tab-${t.id}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
          
          {/* Fixed user section at bottom */}
          <div className="border-t border-gray-100 p-3 bg-white relative">
            {/* Settings Menu Popover */}
            {showSettings && (
              <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50" data-testid="settings-menu">
                <div className="p-1">
                  <button
                    onClick={() => { setTab('plan'); setShowSettings(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    data-testid="settings-plan-btn"
                  >
                    <Crown className="w-4 h-4 text-amber-500" />
                    <span>Manage Plan</span>
                    <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${profile.plan === 'pro' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                      {profile.plan}
                    </span>
                  </button>
                  <button
                    onClick={() => { navigate('/billing'); setShowSettings(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    data-testid="settings-billing-btn"
                  >
                    <Receipt className="w-4 h-4 text-gray-400" />
                    <span>Billing & Invoices</span>
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => { setShowSettings(false); setShowDeleteConfirm(true); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                    data-testid="settings-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Account</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* User Info Card */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {profile.profile?.image_url ? (
                  <img src={profile.profile.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{profile.profile?.name || 'No name'}</p>
                <p className="text-xs text-gray-400 truncate">@{profile.username}</p>
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                data-testid="settings-toggle-btn"
              >
                {showSettings ? <ChevronUp className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Plan Badge */}
            <div className="mt-2 flex items-center justify-between">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${profile.plan === 'pro' ? 'bg-amber-100 text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                {profile.plan} Plan
              </span>
              {profile.plan === 'free' && (
                <button
                  onClick={() => setTab('plan')}
                  className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  Upgrade
                </button>
              )}
            </div>
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-8 max-w-3xl">
          {msg && <div className="mb-4 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold px-4 py-2.5 rounded-lg animate-fade-in" data-testid="success-msg">{msg}</div>}
          {tab === 'profile' && <ProfileTab profile={profile} setProfile={setProfile} showMsg={showMsg} refreshUser={refreshUser} />}
          {tab === 'links' && <LinksTab profile={profile} setProfile={setProfile} showMsg={showMsg} />}
          {tab === 'content' && <ContentTab profile={profile} setProfile={setProfile} showMsg={showMsg} />}
          {tab === 'theme' && <ThemeTab profile={profile} setProfile={setProfile} showMsg={showMsg} />}
          {tab === 'contacts' && <ContactsTab />}
          {tab === 'schedule' && <ScheduleTab profile={profile} setProfile={setProfile} showMsg={showMsg} />}
          {tab === 'analytics' && <AnalyticsTab />}
          {tab === 'plan' && <PlanTab profile={profile} setProfile={setProfile} showMsg={showMsg} loadProfile={loadProfile} />}
        </main>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-account-modal">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
                <button onClick={() => setShowDeleteConfirm(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
                <p className="text-sm text-red-700 font-medium">This action cannot be undone.</p>
                <p className="text-xs text-red-600 mt-1">All your data including your profile, contacts, bookings, and analytics will be permanently deleted.</p>
              </div>
              <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete your account?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" /> Delete Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
